import React from 'react';

import { TranslateFunction } from 'react-localize-redux';

import {
  Typography,
  TypographyVariant,
} from '../../../components/atoms/Typography/Typography';
import {
  Location as OrderAreaIcon,
  OrderCollection as OrderCollectionIcon,
  OrderDelivery as OrderDeliveryIcon,
  OrderGate as OrderGateIcon,
  OrderParking as OrderParkingIcon,
  OrderRoom as OrderRoomIcon,
  OrderTableService as OrderTableServiceIcon,
  OrderVilla as OrderVillaIcon,
} from '../../../components/icons';
import { Currency } from '../../../core/api/general/general.types';
import {
  CollectionRestaurantDetail,
  CollectionRestaurantSummary,
  DeliveryRestaurantDetail,
  DeliveryType,
  PickupLocationType,
  TableServiceCategory,
} from '../../../core/api/restaurant/restaurant.types';
import { DeliveryLocation } from '../../../core/api/user/user.types';
import { AppConfig } from '../../../core/api/whitelabel/whitelabel.types';
import { Async } from '../../../core/types/data.types';
import { Routes } from '../../../core/types/route.types';
import { formatNumberToCurrency } from '../../../utils/basketUtils';
import { formatDateTimeString } from '../../../utils/dateTimeUtils';

const {
  Area,
  DriveThrough,
  Gate,
  Generic,
  House,
  ParkingBay,
  Room,
  Table,
  Villa,
} = TableServiceCategory;

type LocationDetailsType = {
  address: JSX.Element | string;
  icon: JSX.Element;
  title: string;
};

const formatAddress = (
  address: DeliveryLocation,
  variant: TypographyVariant,
  classes: any,
): JSX.Element => {
  const addressLine1 = address?.TwoLinesDisplay[0].trim();
  const addressLine2 = address?.TwoLinesDisplay[1].trim();

  return (
    <>
      <Typography className={classes.address} variant={variant}>
        {`${addressLine1},`}
      </Typography>
      <Typography className={classes.address} variant={variant}>
        {addressLine2}
      </Typography>
    </>
  );
};

export const getCollectionTitle = (
  isTableServiceOnly: boolean,
  tableServiceCategory: TableServiceCategory,
  translate: TranslateFunction,
): string => {
  if (!isTableServiceOnly) {
    return translate('Menu_Location_CollectAt') as string;
  }

  switch (tableServiceCategory) {
    case Generic:
    case Table:
      return translate('Table_service') as string;
    case Villa:
      return translate('Villa_service') as string;
    case House:
      return translate('House_service') as string;
    case Room:
      return translate('Room_service') as string;
    case Area:
      return translate('Area_service') as string;
    case ParkingBay:
      return translate('Parking_bay_service') as string;
    case Gate:
      return translate('Gate_service') as string;
    case DriveThrough:
      return translate('Drive_Through') as string;
    default:
      return translate('Order') as string;
  }
};

export const getCollectionIcon = (
  isTableServiceOnly: boolean,
  tableServiceCategory: TableServiceCategory,
): JSX.Element => {
  if (!isTableServiceOnly) {
    return <OrderCollectionIcon />;
  }

  switch (tableServiceCategory) {
    case House:
    case Villa:
      return <OrderVillaIcon />;
    case Room:
      return <OrderRoomIcon />;
    case Area:
      return <OrderAreaIcon />;
    case DriveThrough:
    case ParkingBay:
      return <OrderParkingIcon />;
    case Gate:
      return <OrderGateIcon />;
    case Generic:
    case Table:
    default:
      return <OrderTableServiceIcon />;
  }
};

export const getDeliveryLocation = (
  deliveryLocations: Async<DeliveryLocation[]>,
  params: Routes['params'],
): DeliveryLocation | null => {
  if (params.type === 'delivery' && deliveryLocations.status === 'success') {
    return (
      deliveryLocations.data.find(
        (dl) => dl.DeliveryLocationId === Number(params.id),
      ) || null
    );
  }

  return null;
};

const getDeliveryLocationDetails = (
  deliveryLocation: DeliveryLocation,
  classes: any,
  variant: TypographyVariant,
  translate: TranslateFunction,
): LocationDetailsType => ({
  address: formatAddress(deliveryLocation, variant, classes),
  icon: <OrderDeliveryIcon />,
  title: translate('Delivery') as string,
});

export const checkIsCollectionRestaurantOnly = (
  configData: AppConfig,
  collectionSummaries: CollectionRestaurantSummary[],
): boolean => {
  if (!collectionSummaries) {
    return false;
  }

  const isCollectionListSkipped = collectionSummaries.some(
    (store) => store.SkipToThisPhysicalRestaurant === true,
  );

  const isCollectionRestaurantOnly =
    !configData.DeliveryEnabled &&
    configData.PickupEnabled &&
    isCollectionListSkipped;

  return isCollectionRestaurantOnly;
};

export const checkIsTableServiceOnly = (
  restaurant: CollectionRestaurantDetail,
): boolean => {
  if (!restaurant.PickupLocationTypes) {
    return false;
  }

  const hasTableService = restaurant.PickupLocationTypes.some(
    (pickupLocation) => pickupLocation === PickupLocationType.TableService,
  );
  const isTableServiceOnly =
    restaurant.PickupLocationTypes.length === 1 && hasTableService;

  return isTableServiceOnly;
};

export const getCollectionLocationDetails = (
  restaurant: CollectionRestaurantDetail,
  classes: any,
  variant: TypographyVariant,
  translate: TranslateFunction,
): LocationDetailsType => {
  const hasTableService = restaurant.PickupLocationTypes?.some(
    (pickupLocation) => pickupLocation === PickupLocationType.TableService,
  );
  const isTableServiceOnly =
    restaurant.PickupLocationTypes?.length === 1 && hasTableService;
  const tableServiceCategory =
    restaurant.TableServiceCatagory || TableServiceCategory.Generic;

  return {
    address: (
      <Typography className={classes.address} variant={variant}>
        {restaurant.PhysicalRestaurantAddress}
      </Typography>
    ),
    icon: getCollectionIcon(isTableServiceOnly, tableServiceCategory),
    title: getCollectionTitle(
      isTableServiceOnly,
      tableServiceCategory,
      translate,
    ),
  };
};

export const getLocationDetails = (
  restaurantData: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  deliveryLocation: DeliveryLocation,
  classes: any,
  variant: TypographyVariant,
  translate: TranslateFunction,
) =>
  restaurantData.DeliveryType === DeliveryType.Delivery
    ? getDeliveryLocationDetails(deliveryLocation, classes, variant, translate)
    : getCollectionLocationDetails(restaurantData, classes, variant, translate);

export const getRestaurantNotes = (
  restaurantData: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  currency: Currency,
  languageCode: string,
  translate: TranslateFunction,
): string[] => {
  const notes = [];

  if (restaurantData.DeliveryType === DeliveryType.Delivery) {
    const deliveryFee = formatNumberToCurrency(
      restaurantData.DeliveryFee,
      currency,
      languageCode,
    );
    const deliveryFeeNote = `${translate(
      'Menu_Header_DeliveryFee',
    )}: ${deliveryFee}`;

    notes.push(deliveryFeeNote);
  }

  if (restaurantData.MinimumOrder > 0) {
    const minOrderAmount = formatNumberToCurrency(
      restaurantData.MinimumOrder,
      currency,
      languageCode,
    );
    const minOrderNote = `${translate(
      'Menu_Header_MinimumOrder',
    )}: ${minOrderAmount}`;

    notes.push(minOrderNote);
  }

  if (
    restaurantData.IsOpen &&
    restaurantData?.ClosingTimeStamp &&
    !restaurantData.OpenTimeMessage
  ) {
    const closingTime = formatDateTimeString(restaurantData.ClosingTimeStamp);
    const openStatusNote = `${translate(
      'Menu_Header_OpenTill',
    )} ${closingTime}`;

    notes.push(openStatusNote);
  }

  return notes;
};
